'use client';

import { c as _c } from "react/compiler-runtime";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/nextjs';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { isHttpError } from '@/api/http-error';
import { useEnvironmentContext } from '@/providers/environment.provider';
import React, { PropsWithChildren } from 'react';
import { useRouter } from 'next/navigation';
import { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { useLogout } from '@/hooks/use-logout';
import { toast } from 'sonner';
import { Duration } from '@/lib/duration';
function is_server_error(http_status: number): boolean {
  return http_status >= 500 && http_status < 600;
}
const CACHE_TIME = Duration.from({
  seconds: 30
}).toMillis();
function isSharedPageDisabled(responseMessage: string | undefined) {
  if (!responseMessage) return false;
  return responseMessage.toLowerCase().includes('shared page is deactivated');
}
type WithDetail = {
  detail?: string;
};
export const QUERY_PARAM_ERROR = 'error';
export const navigateWithError = (router: AppRouterInstance, errorMessage: string) => {
  const encodedError = encodeURIComponent(errorMessage);
  router.push(`/error?${QUERY_PARAM_ERROR}=${encodedError}`);
};
export const ReactQueryProvider = t0 => {
  const $ = _c(9);
  const {
    children
  } = t0;
  const router = useRouter();
  const {
    logout
  } = useLogout();
  let t1;
  if ($[0] !== logout || $[1] !== router) {
    t1 = (error, opts) => {
      const status = isHttpError(error) ? error.status : undefined;
      const data = isHttpError(error) ? error.data : undefined;
      if (data) {
        if (status === 401) {
          if (isSharedPageDisabled((data as WithDetail)?.detail)) {
            navigateWithError(router, "This shared link has been disabled by your organization.");
            toast.error("This shared link has been disabled by your organization.", {
              duration: 6000
            });
            return;
          }
          logout();
          return;
        }
      }
      if (status && is_server_error(status)) {
        console.error("server error", error);
        Sentry.captureException(error);
      } else {
        console.info("client error", error);
      }
      if (opts?.suppressToast) {
        return;
      }
      let errorMessage = status === 403 ? "Insufficient permissions" : "Something went wrong, please try again or contact support if the problem persists";
      const detail = (data as {
        detail?: string;
      } | undefined)?.detail;
      if (detail) {
        if (detail === "duplicate_feature_flag_name") {
          errorMessage = "A feature flag with this id already exists";
        }
      }
      toast.error(errorMessage, {
        duration: 6000
      });
    };
    $[0] = logout;
    $[1] = router;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const onError = t1;
  let t2;
  if ($[3] !== onError) {
    t2 = () => new QueryClient({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
          refetchOnMount: false,
          cacheTime: CACHE_TIME,
          staleTime: CACHE_TIME
        }
      },
      queryCache: new QueryCache({
        onError: (error_0, query) => onError(error_0, {
          suppressToast: query.meta?.suppressErrorToast === true
        })
      }),
      mutationCache: new MutationCache({
        onError: error_1 => onError(error_1)
      })
    });
    $[3] = onError;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const [queryClient] = React.useState(t2);
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ReactQueryDevtoolsGate />;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== children || $[7] !== queryClient) {
    t4 = <QueryClientProvider client={queryClient}>{children}{t3}</QueryClientProvider>;
    $[6] = children;
    $[7] = queryClient;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
};
function ReactQueryDevtoolsGate() {
  const $ = _c(1);
  const {
    showTanstackDevtools
  } = useEnvironmentContext();
  if (!showTanstackDevtools) {
    return null;
  }
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <ReactQueryDevtools position="top-right" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}